import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listWorkspaces, markOnboarded } from '../lib/api';
import { SESSION_KEY, useSession } from '../features/auth/useSession';

interface Slide {
  title: string;
  body: string;
}

/** Shown to someone who owns (or is setting up) a workspace. */
const CREATOR_SLIDES: Slide[] = [
  {
    title: 'Welcome to KatSquare Draft',
    body: 'A calm place to proof and approve print PDFs with your team — comments, versions and sign-off in one flow.',
  },
  {
    title: 'Set up your first magazine',
    body: 'Content is organised as Workspace → Magazine → Month → PDF. Create a magazine in the left sidebar, add a month, then upload a PDF to get going.',
  },
  {
    title: 'Invite your team',
    body: "Add reviewers to comment and approve. Open a PDF and we'll show you the review tools right there on the page.",
  },
];

/** Shown to someone invited into an existing workspace to review. */
const REVIEWER_SLIDES: Slide[] = [
  {
    title: 'Welcome to KatSquare Draft',
    body: "You've been added to a workspace to review PDFs. Here's how it works.",
  },
  {
    title: 'Comment right on the page',
    body: 'Open a PDF and click to leave a comment — drop a pin, draw a box, sketch freehand or highlight text. Mention teammates with @.',
  },
  {
    title: 'Approve & compare versions',
    body: "Approve a PDF when it's ready, and use Compare to see what changed between rounds. Open a PDF and we'll point out each tool on the page.",
  },
];

/**
 * First-run welcome intro. Shows once per account (persisted server-side via
 * user.onboardedAt); both "Skip" and "Get started" mark it seen. Copy adapts to
 * whether the user owns a workspace or was invited to review.
 */
export function WelcomeModal() {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const workspaces = useQuery({ queryKey: ['workspaces'], queryFn: listWorkspaces });
  const [step, setStep] = useState(0);

  const finish = useMutation({
    mutationFn: markOnboarded,
    onSuccess: (u) => queryClient.setQueryData(SESSION_KEY, u),
  });

  // Only real users who haven't seen it; wait for workspaces so the copy is
  // right; never the platform operator.
  if (!user || user.isSuperAdmin || user.onboardedAt || finish.isSuccess) return null;
  if (workspaces.isPending) return null;

  const isCreator =
    (workspaces.data?.length ?? 0) === 0 || (workspaces.data ?? []).some((w) => w.role === 'OWNER');
  const slides = isCreator ? CREATOR_SLIDES : REVIEWER_SLIDES;
  const slide = slides[step]!;
  const last = step === slides.length - 1;
  const done = () => finish.mutate();

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Getting started
          </span>
          <button
            type="button"
            onClick={done}
            disabled={finish.isPending}
            className="text-sm text-neutral-400 hover:text-neutral-700"
          >
            Skip intro
          </button>
        </div>

        <h2 className="text-xl font-semibold text-neutral-900">{slide.title}</h2>
        <p className="mt-2 text-[15px] leading-relaxed text-neutral-600">{slide.body}</p>

        <div className="mt-6 flex items-center justify-between">
          <div className="flex gap-1.5">
            {slides.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-1.5 rounded-full ${i === step ? 'bg-neutral-900' : 'bg-neutral-200'}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="rounded-md border border-neutral-200 px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-50"
              >
                Back
              </button>
            )}
            {last ? (
              <button
                type="button"
                onClick={done}
                disabled={finish.isPending}
                className="rounded-md bg-neutral-900 px-4 py-2 text-sm font-semibold text-white hover:bg-neutral-700"
              >
                {finish.isPending ? 'Finishing…' : 'Get started'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                className="rounded-md bg-neutral-900 px-4 py-2 text-sm font-semibold text-white hover:bg-neutral-700"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
